import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

// ─── Map.prototype.getOrInsertComputed polyfill ──────────────────────────────
// pdfjs-dist (and other modern libs) call Map.prototype.getOrInsertComputed,
// a TC39 2024 proposal that ships natively in Chrome 134+, Firefox 137+, and
// Safari 26.0+. Older Safari (incl. iOS Safari < 26) and certain Safari 26.x
// builds either lack the method or have a buggy implementation, throwing
// `TypeError: this.#t.getOrInsertComputed is not a function` from inside
// pdf.mjs WorkerTransport.#cacheSimpleMethod, leaving the contract canvas
// blank with no signature overlays. Sentry: SUN-RENTAL-ESIGN-WEB-2.
//
// We install a spec-faithful polyfill before any other module runs (the
// import order above guarantees this file executes before App and pdfjs-dist).
// We override BOTH when the method is missing AND when it exists but throws on
// a smoke test — some Safari 26.x builds expose a broken native version that
// fails when called from inside private-field code paths.
(() => {
  if (typeof Map === "undefined") return;
  const proto = Map.prototype as any;
  let needsPolyfill = typeof proto.getOrInsertComputed !== "function";
  if (!needsPolyfill) {
    try {
      const probe = new Map<string, number>();
      const out = proto.getOrInsertComputed.call(probe, "k", () => 1);
      if (out !== 1 || probe.get("k") !== 1) needsPolyfill = true;
    } catch {
      needsPolyfill = true;
    }
  }
  if (!needsPolyfill) return;
  Object.defineProperty(Map.prototype, "getOrInsertComputed", {
    value: function <K, V>(this: Map<K, V>, key: K, callbackfn: (key: K) => V): V {
      if (typeof callbackfn !== "function") {
        throw new TypeError("Map.prototype.getOrInsertComputed: callbackfn must be callable");
      }
      if (this.has(key)) return this.get(key) as V;
      const value = callbackfn(key);
      this.set(key, value);
      return value;
    },
    writable: true,
    configurable: true,
    enumerable: false,
  });
})();

// Sentry: only initialize when a DSN is provided at build time. In dev
// VITE_SENTRY_DSN_WEB is unset, so init is a no-op and we don't ship
// errors from local development.
const dsn = import.meta.env.VITE_SENTRY_DSN_WEB as string | undefined;
if (dsn) {
  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text and block all media so PII (customer names,
        // addresses, signatures) never leaves the browser.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    environment: import.meta.env.MODE,
  });
}

if (!window.location.hash) {
  window.location.hash = "#/";
}

createRoot(document.getElementById("root")!).render(<App />);
